import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase-config';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [allScores, setAllScores] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: '30daysAgo',
    endDate: 'today'
  });
  const [analytics, setAnalytics] = useState({
    totalGames: 0,
    totalPlayers: 0,
    daysInRange: 0,
    gamesPerDay: 0,
    avgCompletionTime: 0,
    avgMoves: 0,
    modeBreakdown: {},
    difficultyBreakdown: {},
    dailyActivity: [],
    topPlayers: [],
    recentGames: []
  });

  // Convert date range preset to actual dates
  const getDateRangeDates = useCallback((startDate, endDate) => {
    const now = new Date();
    let start, end;

    // Parse end date - set to end of day (23:59:59.999)
    if (endDate === 'today') {
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    } else if (endDate === 'yesterday') {
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      end = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
    } else if (endDate === 'thisMonthStart') {
      // This is actually used as an end date for "last month"
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(monthStart.getTime() - 1); // Last millisecond of previous month
    } else {
      end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
    }

    // Parse start date - set to start of day (00:00:00.000)
    if (startDate === 'today') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    } else if (startDate === 'yesterday') {
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
    } else if (startDate === '7daysAgo') {
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      start = new Date(sevenDaysAgo.getFullYear(), sevenDaysAgo.getMonth(), sevenDaysAgo.getDate(), 0, 0, 0, 0);
    } else if (startDate === '30daysAgo') {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      start = new Date(thirtyDaysAgo.getFullYear(), thirtyDaysAgo.getMonth(), thirtyDaysAgo.getDate(), 0, 0, 0, 0);
    } else if (startDate === '90daysAgo') {
      const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      start = new Date(ninetyDaysAgo.getFullYear(), ninetyDaysAgo.getMonth(), ninetyDaysAgo.getDate(), 0, 0, 0, 0);
    } else if (startDate === 'thisMonthStart') {
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    } else if (startDate === 'lastMonthStart') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
    } else if (startDate === 'thisYearStart') {
      start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
    } else {
      start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
    }

    return { start, end };
  }, []);

  // Calculate analytics from scores
  const calculateAnalytics = useCallback((scores) => {
    if (scores.length === 0) {
      setAnalytics({
        totalGames: 0,
        totalPlayers: 0,
        daysInRange: 0,
        gamesPerDay: 0,
        avgCompletionTime: 0,
        avgMoves: 0,
        modeBreakdown: {},
        difficultyBreakdown: {},
        dailyActivity: [],
        topPlayers: [],
        recentGames: []
      });
      return;
    }

    const now = new Date();
    const { start: rangeStart, end: rangeEnd } = getDateRangeDates(dateRange.startDate, dateRange.endDate);

    console.log('=== Date Range Calculation ===');
    console.log('Date range:', dateRange);
    console.log('Parsed range:', {
      start: rangeStart.toISOString(),
      end: rangeEnd.toISOString()
    });
    console.log('Total scores in DB:', scores.length);

    // Filter scores by date range
    const filteredScores = scores.filter(s => {
      const timestamp = s.timestamp?.toDate ? s.timestamp.toDate() : new Date(s.timestamp);
      return timestamp >= rangeStart && timestamp <= rangeEnd;
    });

    console.log('Filtered scores:', filteredScores.length);

    // Show first and last score timestamps for debugging
    if (filteredScores.length > 0) {
      const firstScore = filteredScores[filteredScores.length - 1];
      const lastScore = filteredScores[0];
      const firstTime = firstScore.timestamp?.toDate ? firstScore.timestamp.toDate() : new Date(firstScore.timestamp);
      const lastTime = lastScore.timestamp?.toDate ? lastScore.timestamp.toDate() : new Date(lastScore.timestamp);
      console.log('First score date:', firstTime.toISOString());
      console.log('Last score date:', lastTime.toISOString());
    } else {
      console.log('No scores in selected date range');
    }
    console.log('===========================');

    // Always use filtered scores (even if empty) - don't fall back to all scores
    const scoresToAnalyze = filteredScores;

    // Unique players (only count games with playerName)
    const uniquePlayers = new Set(
      scoresToAnalyze
        .filter(s => s.playerName)
        .map(s => s.playerName.toLowerCase())
    ).size;

    // Calculate days in selected range for "per day" calculations
    const daysBetweenRange = Math.max(1, Math.ceil((rangeEnd - rangeStart) / (24 * 60 * 60 * 1000)));

    // Averages
    const avgTime = scoresToAnalyze.reduce((sum, s) => sum + (s.time || 0), 0) / scoresToAnalyze.length;
    const avgMoves = scoresToAnalyze.reduce((sum, s) => sum + (s.moves || 0), 0) / scoresToAnalyze.length;

    // Mode breakdown
    const modeBreakdown = scoresToAnalyze.reduce((acc, s) => {
      acc[s.mode] = (acc[s.mode] || 0) + 1;
      return acc;
    }, {});

    // Difficulty breakdown
    const difficultyBreakdown = scoresToAnalyze.reduce((acc, s) => {
      if (s.difficulty) {
        acc[s.difficulty] = (acc[s.difficulty] || 0) + 1;
      }
      return acc;
    }, {});

    // Daily activity based on date range
    const daysToShow = Math.min(daysBetweenRange, 90); // Max 90 days
    const dailyActivity = [];
    for (let i = daysToShow - 1; i >= 0; i--) {
      const date = new Date(rangeEnd.getTime() - i * 24 * 60 * 60 * 1000);
      const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const dateEnd = new Date(dateStart.getTime() + 24 * 60 * 60 * 1000);

      const gamesOnDate = scoresToAnalyze.filter(s => {
        const timestamp = s.timestamp?.toDate ? s.timestamp.toDate() : new Date(s.timestamp);
        return timestamp >= dateStart && timestamp < dateEnd;
      }).length;

      dailyActivity.push({
        date: dateStart.toISOString().split('T')[0],
        games: gamesOnDate
      });
    }

    // Top players by best time (from filtered scores that have playerName)
    const gamesWithNames = scoresToAnalyze.filter(s => s.playerName);
    const playerBestTimes = {};
    gamesWithNames.forEach(s => {
      const player = s.playerName;
      if (!playerBestTimes[player] || s.time < playerBestTimes[player].time) {
        playerBestTimes[player] = {
          playerName: player,
          time: s.time,
          mode: s.mode,
          difficulty: s.difficulty,
          moves: s.moves
        };
      }
    });

    const topPlayers = Object.values(playerBestTimes)
      .sort((a, b) => a.time - b.time)
      .slice(0, 10);

    // Recent games (from filtered scores, prioritize those with playerName for leaderboard view)
    const recentGames = gamesWithNames.slice(0, 20);

    setAnalytics({
      totalGames: scoresToAnalyze.length,
      totalPlayers: uniquePlayers,
      daysInRange: daysBetweenRange,
      gamesPerDay: (scoresToAnalyze.length / daysBetweenRange).toFixed(1),
      avgCompletionTime: avgTime,
      avgMoves: avgMoves,
      modeBreakdown,
      difficultyBreakdown,
      dailyActivity,
      topPlayers,
      recentGames
    });
  }, [dateRange, getDateRangeDates]);

  // Fetch all games from Firebase
  // Try 'games' collection first (new), fallback to 'scores' collection (old)
  const fetchAllScores = useCallback(async () => {
    try {
      setLoading(true);
      let allGames = [];

      // Try fetching from 'games' collection first (new system - tracks all completions)
      try {
        const gamesRef = collection(db, 'games');
        const gamesQuery = query(gamesRef, orderBy('timestamp', 'desc'));
        const gamesSnapshot = await getDocs(gamesQuery);

        gamesSnapshot.forEach((doc) => {
          allGames.push({
            id: doc.id,
            ...doc.data()
          });
        });

        console.log(`Fetched ${allGames.length} games from 'games' collection`);
      } catch (gamesError) {
        console.log('Games collection not available yet:', gamesError.message);
      }

      // Also fetch from 'scores' collection (old system - only leaderboard submissions)
      // This ensures we don't lose historical data
      try {
        const scoresRef = collection(db, 'scores');
        const scoresQuery = query(scoresRef, orderBy('timestamp', 'desc'));
        const scoresSnapshot = await getDocs(scoresQuery);

        scoresSnapshot.forEach((doc) => {
          allGames.push({
            id: doc.id,
            ...doc.data(),
            isLeaderboardSubmission: true // Mark scores from old collection
          });
        });

        console.log(`Fetched ${scoresSnapshot.size} scores from 'scores' collection`);
      } catch (scoresError) {
        console.error('Error fetching scores:', scoresError);
      }

      // Remove duplicates (in case a game exists in both collections)
      // Prefer the 'games' collection version
      const uniqueGames = Array.from(
        new Map(allGames.map(game => [game.id, game])).values()
      );

      console.log(`Total unique games: ${uniqueGames.length}`);
      setAllScores(uniqueGames);
      calculateAnalytics(uniqueGames);
    } catch (error) {
      console.error('Error fetching game data:', error);
    } finally {
      setLoading(false);
    }
  }, [calculateAnalytics]);

  // Refresh data
  const refresh = async () => {
    await fetchAllScores();
  };

  useEffect(() => {
    fetchAllScores();
  }, [fetchAllScores]);

  // Recalculate analytics when date range changes
  useEffect(() => {
    if (allScores.length > 0) {
      calculateAnalytics(allScores);
    }
  }, [dateRange, allScores, calculateAnalytics]);

  const value = {
    loading,
    analytics,
    allScores,
    dateRange,
    setDateRange,
    refresh
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
